import {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {pAlert} from '../../common/component/PAlert';
import {REGION, WEB_HOST} from '../../common/constant';
import {Page} from '../../common/constant/page';
import useTypedSelector from '../../common/hook/useTypedSelector';
import useUserAgent from '../../common/hook/useUserAgent';
import {IRegion} from '../../common/type';
import {ButtonSet} from '../../common/type/alert';
import {useFetchInfo} from '../../common/util/fetch';
import {getUrl, routeTo} from '../../common/util/PageRouter';
import {deleteLocalStorage} from '../../common/util/storage';
import {getKakaoSdk} from '../../commonSub/function';
import {
  handleClickIntegration,
  RETURN_PAGE_INFO_LS_KEY,
  saveReturnPage,
  sendAuthEvent,
  sendAuthViewImp,
  submitSocialOAuth,
  TERMS_CHECKED_INFO_LS_KEY,
} from '../common';
import AuthModal from '../component/AuthModal';
import AuthModalKr from '../component/AuthModalKr';
import {actions, ActionType} from '../state/action';
import {IAuthModalType, ILoginMethod} from '../type';

export default function Auth() {
  const isKorea = REGION === 'kor';
  const dispatch = useDispatch();
  const userAgent = useUserAgent();
  const {isFirstFetched} = useFetchInfo(ActionType.FetchLastLoginAuth);
  const [isCherryPicking, authModal, lastLoginAuth, disabled, isExistAlert] =
    useTypedSelector(state => [
      state.auth.isCherryPicking,
      state.auth.authModal,
      state.auth.lastLoginAuth,
      HIDE_MODAL_PAGE_LIST.includes(state.common.layoutProps.page),
      !!state.common.pAlerts.length,
    ]);
  const [modalType, setModalType] = useState<IAuthModalType>();
  useEffect(() => {
    if (!isKorea && authModal.show) {
      dispatch(actions.fetchUserCherryPicking());
      dispatch(actions.fetchLastLoginAuth());
    }
  }, [authModal, dispatch, isKorea]);

  useEffect(() => {
    if (isFirstFetched && authModal.show) {
      const customProps = lastLoginAuth
        ? {event_layer: 'popup', popup_type: 'recent_login'}
        : {
            popup_type: 'login',
          };
      sendAuthViewImp({customProps, viewImpContents: {name: 'popup'}});
      setModalType(lastLoginAuth ? 'lastLogin' : 'login');
    }
  }, [lastLoginAuth, isFirstFetched, authModal]);

  const requestLoginSns = (loginMethod: ILoginMethod) => {
    deleteLocalStorage({key: TERMS_CHECKED_INFO_LS_KEY});
    saveReturnPage();
    userAgent && submitSocialOAuth({loginMethod, userAgent});
  };

  const onClickLoginSns = async (loginMethod: ILoginMethod) => {
    const params = lastLoginAuth
      ? {
          actionName: 'popup_recent_login',
          customProps: {
            event_layer: 'popup',
            login_type: LOGIN_TYPE_MAP[loginMethod],
            popup_type: 'recent_login',
          },
        }
      : {
          actionName: 'popup_login',
          customProps: {
            event_layer: 'popup',
            login_type: LOGIN_TYPE_MAP[loginMethod],
            popup_type: 'login',
          },
        };
    sendAuthEvent(params);

    if (isCherryPicking) {
      // 동일 DeviceId로 회원가입한 이력이 있을 경우
      requestLoginSns(loginMethod);
    } else {
      sendAuthViewImp({
        customProps: {popup_type: 'signup_app_download'},
        viewImpContents: {name: 'popup'},
      });
      const {
        okClicked: continueAtAppClicked,
        closeClicked: continueAtWebClicked,
        isDimmedClicked,
      } = await pAlert({
        title: t => t('new_user_login_popup_web'),
        buttonSet: ButtonSet.CancelAndConfirm,
        labelClose: t => t('new_user_login_popup_continue_web'),
        labelConfirm: t => t('new_user_login_popup_button_web'),
        isForced: true,
      });

      sendAuthEvent({
        actionName: continueAtWebClicked ? 'popup_conitnue' : 'popup_app',
        customProps: {
          event_layer: 'popup',
          popup_type: 'signup_app_download',
        },
      });

      if (continueAtWebClicked) {
        requestLoginSns(loginMethod);
      } else if (continueAtAppClicked) {
        routeTo(CHERRY_PICKING_APP_ONE_LINK_MAP[REGION]);
      }

      onClickClose(isDimmedClicked, true);
    }
  };

  const onClickLoginKakaoSdk = () => {
    sendAuthEvent({
      actionName: 'popup_login',
      customProps: {
        event_layer: 'popup',
        login_type: 'apptoapp',
        popup_type: 'login',
      },
    });
    saveReturnPage();
    const redirectUri = `${WEB_HOST}${getUrl({page: Page.AuthOAuth})}`;
    getKakaoSdk().Auth.authorize({redirectUri});
  };

  const onClickLoginKakaoDirect = () => {
    sendAuthEvent({
      actionName: 'popup_login',
      customProps: {
        event_layer: 'popup',
        login_type: 'typing',
        popup_type: 'login',
      },
    });
    onClickLoginSns('KA');
  };

  const onClickClose = (
    isDimmedClicked?: boolean,
    notDeleteReturnPageInfo?: boolean
  ) => {
    !notDeleteReturnPageInfo &&
      deleteLocalStorage({key: RETURN_PAGE_INFO_LS_KEY});
    isCherryPicking &&
      sendAuthEvent({
        actionName: 'popup_close',
        customProps: {
          event_layer: isDimmedClicked ? 'dimmed' : 'popup',
          popup_type: lastLoginAuth ? 'recent_login' : 'login',
        },
      });
    authModal.pageInfoAfterClose &&
      routeTo(authModal.pageInfoAfterClose, {isReplace: true});

    dispatch(
      actions.setValue('authModal', {
        show: false,
        pageInfoAfterClose: undefined,
      })
    );
  };

  const onClickIntegration = () => {
    sendAuthEvent({
      actionName: 'popup_used_daumid',
      customProps: {
        event_layer: 'popup',
        popup_type: 'login',
      },
    });
    handleClickIntegration();
  };

  const goLoginModal = () => {
    sendAuthEvent({
      actionName: 'popup_other_login',
      customProps: {
        event_layer: 'popup',
        popup_type: 'recent_login',
      },
    });
    setModalType('login');
  };

  if (disabled) {
    return null;
  }

  return isKorea ? (
    <AuthModalKr
      authModal={authModal}
      isExistAlert={isExistAlert}
      onClickLoginKakaoSdk={onClickLoginKakaoSdk}
      onClickLoginKakaoDirect={onClickLoginKakaoDirect}
      onClickClose={onClickClose}
      onClickIntegration={onClickIntegration}
    />
  ) : (
    <AuthModal
      modalType={modalType}
      authModal={authModal}
      isExistAlert={isExistAlert}
      lastLoginAuth={lastLoginAuth}
      onClickLoginSns={onClickLoginSns}
      goLoginModal={goLoginModal}
      onClickClose={onClickClose}
    />
  );
}

const HIDE_MODAL_PAGE_LIST = [
  Page.AuthIntegration,
  Page.AuthOAuth,
  Page.AuthTerms,
];

const LOGIN_TYPE_MAP: {[key in ILoginMethod]: string} = {
  FB: 'facebook',
  GG: 'google',
  AP: 'apple',
  LN: 'line',
  KA: 'typing',
  DA: 'daum',
};

const CHERRY_PICKING_APP_ONE_LINK_MAP: {
  [key in IRegion]: string;
} = {
  // routeTo에 넘기기 위해서는 string이 필수이다. global 에서만 호출될 거라 kor은 안심해도 된다.
  kor: '',
  twn: 'https://kakaowebtoon.onelink.me/FYzQ/kwpfa848',
  tha: 'https://kakaowebtoon.onelink.me/FYzQ/y2fprw3k',
  idn: 'https://kakaowebtoon.onelink.me/FYzQ/5hlbjgrb',
};
